<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import {
  abrirPainel,
  useTelefone,
} from 'dashboard/components-next/telefone/estado';

defineProps({
  isMoreOpen: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['openMore']);

// A BARRA FLUTUANTE (24/10/2026, Victor: "achei bem melhor… esse nosso tem ficado uma linha
// estranha embaixo"). Era uma faixa navy colada no fim da tela, com borda em cima; no iPhone
// instalado sobrava uma faixa escura abaixo dela (a "folga", que o código tentava compensar
// encostando os ícones). Agora é uma cápsula clara que flutua sobre o chão da página, com
// margem dos lados e embaixo, e a aba ativa em pílula azul-clara: não há faixa para sobrar.

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');

const TABS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'i-lucide-layout-dashboard',
    routeName: 'legal_dashboard',
    activeOn: ['legal_dashboard'],
  },
  {
    key: 'chat',
    label: 'Chat',
    icon: 'i-lucide-message-circle',
    routeName: 'home',
    activeOn: [
      'home',
      'inbox_dashboard',
      'inbox_conversation',
      'inbox_view',
      'inbox_view_conversation',
      // 06/10/2026: os nomes antigos (`label_view`, `team_view`...) não
      // existiam — "Chat" apagava ao abrir etiqueta, time, menções etc.
      'conversation_through_inbox',
      'label_conversations',
      'conversations_through_label',
      'team_conversations',
      'conversations_through_team',
      'folder_conversations',
      'conversations_through_folders',
      'conversation_mentions',
      'conversation_through_mentions',
      'conversation_unattended',
      'conversation_through_unattended',
      'conversation_participating',
      'conversation_through_participating',
    ],
  },
  {
    key: 'interno',
    label: 'Interno',
    icon: 'i-lucide-messages-square',
    routeName: 'internal_chat',
    activeOn: ['internal_chat', 'internal_chat_detail'],
  },
  // advup PWA mobile (2026-07-27) — Pipeline tirado da bottom nav, decisão
  // explícita do Victor: Kanban não é fluxo mobile (drag-and-drop nativo
  // não funciona em touch, ver Fase 3 do plano de PWA — "não é pra quando
  // a pessoa tiver tempo, é pro computador mesmo"). Continua acessível
  // pela folha "Mais", só sai das 4 abas fixas — prioridade mobile real é
  // Dashboard/Chat/Interno/Mais.
];

// advup PWA (Victor, 25/09/2026): quem tem ramal ganha "Telefone" no lugar de
// "Dashboard" — abre o discador. O Dashboard continua na folha "Mais".
const telefone = useTelefone();
const temRamal = computed(
  () => !['inicio', 'sem_ramal'].includes(telefone.fase)
);
const TAB_TELEFONE = {
  key: 'telefone',
  label: 'Telefone',
  icon: 'i-lucide-phone',
  activeOn: [],
  telefone: true,
};
const abas = computed(() =>
  temRamal.value ? [TAB_TELEFONE, ...TABS.slice(1)] : TABS
);
const tocando = computed(() =>
  telefone.chamadas.some(c => c.fase === 'tocando')
);

function isActive(tab) {
  if (tab.telefone) return telefone.painelAberto;
  // com o discador aberto, só a aba Telefone acende
  return !telefone.painelAberto && tab.activeOn.includes(route.name);
}

function navigate(tab) {
  if (tab.telefone) {
    abrirPainel();
    return;
  }
  abrirPainel(false);
  router.push({ name: tab.routeName, params: { accountId: accountId.value } });
}
</script>

<template>
  <!-- advup PWA mobile (2026-07-27) — padding-bottom = safe-area-inset-bottom
       (0px em navegador comum/desktop, valor real do home indicator em
       standalone iPhone). min-height 4rem vira altura MÍNIMA do conteúdo
       clicável; a barra cresce pra baixo com o inset, sem espremer os
       ícones. Classe (não style inline) pra passar do lint
       vue/no-static-inline-styles. -->
  <nav class="advup-barra-flutuante sm:hidden">
    <button
      v-for="tab in abas"
      :key="tab.key"
      type="button"
      class="advup-barra-flutuante__aba"
      :class="{
        'advup-barra-flutuante__aba--on': isActive(tab),
        'advup-nav-tocando': tab.telefone && tocando,
      }"
      :aria-current="isActive(tab) ? 'page' : undefined"
      @click="navigate(tab)"
    >
      <span class="advup-barra-flutuante__ico" :class="tab.icon" />
      <span class="advup-barra-flutuante__rot">{{ tab.label }}</span>
    </button>
    <!-- advup — missão PWA mobile 2026-07-20: bottom-nav só cobria 4 dos
    ~20 módulos (Agenda, Financeiro, Processo, GED, Contatos, Relatórios,
    Configurações... ficavam sem nenhum jeito de abrir no celular, já que
    a nav horizontal de desktop é `hidden` abaixo de 640px). "Mais" abre o
    mesmo menu completo que existe no topo, dentro de uma folha inferior. -->
    <button
      type="button"
      class="advup-barra-flutuante__aba"
      :class="{ 'advup-barra-flutuante__aba--on': isMoreOpen }"
      :aria-expanded="String(isMoreOpen)"
      @click="emit('openMore')"
    >
      <span class="advup-barra-flutuante__ico i-lucide-menu" />
      <span class="advup-barra-flutuante__rot">{{ t('SIDEBAR.MORE') }}</span>
    </button>
  </nav>
</template>

<style scoped>
/* Só no celular (<640px, como sempre foi). O `display` mora AQUI e não no `sm:hidden` do
   template: a regra scoped tem mais especificidade que o utilitário do Tailwind, e com
   `display: flex` solto a barra aparecia no computador (24/10/2026, Victor). */
.advup-barra-flutuante {
  display: none;
}

@media (width < 640px) {
  .advup-barra-flutuante {
    display: flex;
  }
}

.advup-barra-flutuante {
  position: fixed;
  right: var(--esp-3);
  bottom: var(--esp-2);
  left: var(--esp-3);
  z-index: 50;
  align-items: stretch;
  gap: 2px;
  min-height: 3.75rem;
  padding: var(--esp-1);
  border-radius: 22px;
  background: rgb(var(--solid-1) / 92%);
  box-shadow:
    0 0 0 1px rgb(var(--slate-4)),
    0 10px 28px rgb(15 23 42 / 14%);
  backdrop-filter: blur(16px) saturate(1.4);
}

/* O conteúdo acaba logo acima da barra: um esmaecer curto no chão da tela (Dashboard.vue,
   --chao-celular) em vez de um corte seco. */
.advup-barra-flutuante::before {
  content: '';
  position: absolute;
  right: calc(var(--esp-3) * -1);
  bottom: calc(100% + var(--esp-2));
  left: calc(var(--esp-3) * -1);
  height: var(--esp-5);
  background: linear-gradient(
    to bottom,
    transparent,
    var(--chao-celular, transparent)
  );
  pointer-events: none;
}

.advup-barra-flutuante__aba {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--esp-1);
  min-width: 0;
  padding: var(--esp-1) 2px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  color: rgb(var(--slate-11));
  transition:
    background var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva);
}

.advup-barra-flutuante__aba:active {
  background: rgb(var(--slate-3));
}

.advup-barra-flutuante__aba--on,
.advup-barra-flutuante__aba--on:active {
  background: rgb(var(--blue-3));
  color: rgb(var(--blue-11));
}

.advup-barra-flutuante__aba:focus-visible {
  outline: 2px solid rgb(var(--blue-8));
  outline-offset: 1px;
}

.advup-barra-flutuante__ico {
  flex: none;
  width: 1.375rem;
  height: 1.375rem;
}

.advup-barra-flutuante__rot {
  max-width: 100%;
  overflow: hidden;
  font-size: 11px;
  font-weight: var(--peso-forte);
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* a aba Telefone pulsa enquanto uma ligação toca */
.advup-nav-tocando {
  color: rgb(var(--teal-9)) !important;
  animation: advup-nav-toca 1s ease-in-out infinite;
}
@keyframes advup-nav-toca {
  50% {
    transform: scale(1.12);
  }
}
@media (prefers-reduced-motion: reduce) {
  .advup-nav-tocando {
    animation: none;
  }

  .advup-barra-flutuante__aba {
    transition: none;
  }
}
</style>
